import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { services } from "../data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  origin: "",
  destination: "",
  weight: "",
  service: "",
  notes: "",
};

const inputCls =
  "w-full h-12 bg-white border border-blue-900/15 px-4 text-sm font-medium text-navy placeholder:text-navy/35 focus:outline-none focus:ring-2 focus:ring-royal-bright focus:border-royal-bright transition-[box-shadow,border-color] duration-150";

const QuoteForm = ({ dark = false }) => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request received", {
        description: "A live agent will call you within minutes — 24/7.",
      });
      setForm(initial);
    } catch (err) {
      toast.error("Could not send your request", {
        description: "Please call (800) 654-9403 — we answer around the clock.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="quote-form"
      className="grid grid-cols-1 sm:grid-cols-2 gap-4"
    >
      <input
        required
        data-testid="quote-name-input"
        placeholder="Your name *"
        value={form.name}
        onChange={set("name")}
        className={inputCls}
      />
      <input
        data-testid="quote-company-input"
        placeholder="Company"
        value={form.company}
        onChange={set("company")}
        className={inputCls}
      />
      <input
        required
        type="email"
        data-testid="quote-email-input"
        placeholder="Email *"
        value={form.email}
        onChange={set("email")}
        className={inputCls}
      />
      <input
        required
        type="tel"
        data-testid="quote-phone-input"
        placeholder="Phone *"
        value={form.phone}
        onChange={set("phone")}
        className={inputCls}
      />
      <input
        required
        data-testid="quote-origin-input"
        placeholder="Origin city / zip *"
        value={form.origin}
        onChange={set("origin")}
        className={inputCls}
      />
      <input
        required
        data-testid="quote-destination-input"
        placeholder="Destination city / zip *"
        value={form.destination}
        onChange={set("destination")}
        className={inputCls}
      />
      <input
        data-testid="quote-weight-input"
        placeholder="Weight / pallets (e.g. 2 pallets, 1,800 lbs)"
        value={form.weight}
        onChange={set("weight")}
        className={inputCls}
      />
      <select
        required
        data-testid="quote-service-select"
        value={form.service}
        onChange={set("service")}
        className={`${inputCls} ${form.service ? "" : "text-navy/35"}`}
      >
        <option value="" disabled>
          Service needed *
        </option>
        {services.map((s) => (
          <option key={s.slug} value={s.name}>
            {s.name}
          </option>
        ))}
        <option value="Not sure">Not sure — recommend for me</option>
      </select>
      <textarea
        data-testid="quote-notes-input"
        placeholder="Anything else we should know? Deadline, dock hours, liftgate…"
        value={form.notes}
        onChange={set("notes")}
        rows={4}
        className={`${inputCls} sm:col-span-2 h-auto py-3 resize-none`}
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="quote-submit-button"
        className="sm:col-span-2 mt-2 flex items-center justify-center gap-3 h-14 bg-royal text-white font-display text-lg font-bold tracking-wide shadow-[5px_5px_0px_#040B16] hover:shadow-[2px_2px_0px_#040B16] hover:translate-x-[3px] hover:translate-y-[3px] disabled:opacity-60 disabled:pointer-events-none transition-[box-shadow,transform] duration-150"
      >
        {loading ? (
          <Loader2 className="animate-spin" size={20} />
        ) : (
          <>
            REQUEST MY QUOTE <ArrowRight size={18} />
          </>
        )}
      </button>
      <p className="sm:col-span-2 font-mono text-[11px] tracking-widest uppercase text-navy/40">
        No account. No registration. No bots — a human calls you back.
      </p>
    </form>
  );
};

export default QuoteForm;
